import * as React from 'react'
import styled from 'styled-components'
import Image from 'gatsby-image/withIEPolyfill'
import { Ingredient } from '../../data/Ingredient'
import { constants } from '../../constants'
import TextBlock from '../common/TextBlock'
import SectionTitle from '../common/SectionTitle'
import { SpanWithArrow } from '../common/LinkWithArrow'
import { Language } from '../../Language'
import { FontsContext } from '../../context'
import { withPrefix } from 'gatsby'

const MAX_LENGTH = {
  en: 125,
  jp: 85,
}

const removeUntilSpace = (text: string): string => {
  if (text[text.length - 1] === ' ') {
    return text
  }

  return removeUntilSpace(text.substring(0, text.length - 1))
}

const trimDescription = (text: string, language: Language) => {
  const newLinesRemoved = text.replace(/\n/g, ' ')
  const maxLength = MAX_LENGTH[language]

  if (newLinesRemoved.length <= maxLength) {
    return newLinesRemoved
  }

  if (language === Language.JP) {
    return `${newLinesRemoved.substring(0, maxLength)} ...`
  }

  return `${removeUntilSpace(newLinesRemoved.substring(0, maxLength))} ...`
}

interface Props {
  ingredient: Ingredient
  fluidImage: any
  language: Language
  messages: any
}

const IngredientItem: React.FC<Props> = ({
  ingredient,
  fluidImage,
  language,
  messages,
}) => {
  const fonts = React.useContext(FontsContext)

  return (
    <StyledLi>
      <StyledLink
        href={withPrefix(`/${language}/ingredients/${ingredient.path}/`)}
      >
        <ImageWrapper>
          <Image fluid={fluidImage} alt={ingredient.name} />
        </ImageWrapper>
        <TextWrapper>
          <Title fonts={fonts} settings={fonts.texts.title4}>
            {ingredient.name}
          </Title>
          <TextBlock>
            {trimDescription(ingredient.whatAreThey, language)}
          </TextBlock>
          <LinkWrapper>
            <SpanWithArrow settings={fonts.texts.arrowLink} fonts={fonts}>
              {messages.ingredients.learnMore}
            </SpanWithArrow>
          </LinkWrapper>
        </TextWrapper>
      </StyledLink>
    </StyledLi>
  )
}

const StyledLink = styled.a`
  text-decoration: none;
  position: relative;
  display: block;

  &:hover {
    opacity: 0.6;
    text-decoration: none;
  }

  &:visited,
  &:active {
    text-decoration: none;
  }

  @media only screen and (min-width: ${constants.breakpoints.pc}) {
    display: flex;
    align-items: center;
  }
`

const StyledLi = styled.li`
  margin-bottom: ${constants.spacing.sp.large};

  @media only screen and (min-width: ${constants.breakpoints.pc}) {
    margin-bottom: 64px;
  }
`

const ImageWrapper = styled.div`
  position: absolute;
  width: 84px;
  height: 84px;
  top: 0;
  left: 0;
  border-radius: 50%;
  overflow: hidden;

  @media only screen and (min-width: ${constants.breakpoints.pc}) {
    position: static;
    flex: 0 0 220px;
    width: 220px;
    height: 220px;
  }
`

const TextWrapper = styled.div`
  @media only screen and (min-width: ${constants.breakpoints.pc}) {
    padding-left: 48px;
  }
`

const Title = styled(SectionTitle)`
  margin-bottom: ${constants.spacing.sp.small};
  text-align: left;
  padding-left: 96px;
  height: 84px;
  display: flex;
  align-items: center;

  @media only screen and (min-width: ${constants.breakpoints.pc}) {
    margin-bottom: 12px;
    height: auto;
    padding-left: 0;
  }
`

const LinkWrapper = styled.div`
  margin-top: ${constants.spacing.sp.small};

  @media only screen and (min-width: ${constants.breakpoints.pc}) {
    text-align: right;
  }
`

export default IngredientItem
